import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import './SettingStyle.css'
import useSettings from './useSettings'
import { SettingsHeaderProvider } from './SettingsHeader.context'
import { SETTINGS_NAV_ICONS } from './settingsNavIcons'
import { EmptyState, Search } from '@enamelai/design-system'
import { ReactComponent as CaretIcon } from '@assets/images/Settings/settings-nav-caret.svg'
import { ReactComponent as ToggleAllIcon } from '@assets/images/Settings/settings-nav-toggle-all.svg'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { getEnv } from '@utils/env'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'

const APP_VERSION = getEnv('REACT_APP_VERSION', '')

export type SettingsOutletContext = {
  isPageReady: boolean
}

const SETTINGS_LOADING_CONTAINER = '.settings-container-page'

function isSettingsHubPath(pathname: string): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname
  return normalized === '/settings'
}

function Settings(): React.ReactElement {
  const hooks = useSettings()
  const location = useLocation()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)
  const isSettingsHub = isSettingsHubPath(location.pathname)

  useLayoutEffect(() => {
    if (!isSettingsHub) {
      hideLoading(SETTINGS_LOADING_CONTAINER)
      if (!isPageReadyRef.current) {
        isPageReadyRef.current = true
        setIsPageReady(true)
      }
      return
    }
    if (isPageReadyRef.current) return
    showLoading({
      container: SETTINGS_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [isSettingsHub, showLoading, hideLoading])

  useEffect(() => {
    if (!isSettingsHub || isPageReadyRef.current) return
    let cancelled = false
    const startedAt = Date.now()
    showLoading({
      container: SETTINGS_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
    void waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS).then(() => {
      if (cancelled) return
      isPageReadyRef.current = true
      setIsPageReady(true)
      hideLoading(SETTINGS_LOADING_CONTAINER)
    })
    return () => {
      cancelled = true
    }
  }, [isSettingsHub, hideLoading, showLoading])

  useEffect(() => {
    return () => {
      hideLoading(SETTINGS_LOADING_CONTAINER)
    }
  }, [hideLoading])

  const highlightText = (
    text: string,
    shouldHighlight: boolean,
  ): React.ReactNode => {
    const normalizedQuery = hooks.searchQuery.trim().toLowerCase()
    if (!normalizedQuery || !shouldHighlight) {
      return text
    }

    const lowerText = text.toLowerCase()
    const index = lowerText.indexOf(normalizedQuery)

    if (index === -1) {
      return <span className="scp-search-highlight">{text}</span>
    }

    const before = text.slice(0, index)
    const match = text.slice(index, index + normalizedQuery.length)
    const after = text.slice(index + normalizedQuery.length)

    return (
      <>
        {before}
        <span className="scp-search-highlight">{match}</span>
        {after}
      </>
    )
  }

  const outletContext: SettingsOutletContext = {
    isPageReady,
  }

  return (
    <SettingsHeaderProvider value={hooks.headerContextValue}>
      <div
        className={`settings-container-page${isPageReady ? ' is-ready' : ''}`}
      >
        <div className="scp-main-content-wrapper">
          {hooks.showSidebar && (
            <aside className="scp-side-panel" aria-label="Settings">
              <div className="scp-side-panel-top">
                <div className="scp-side-panel-title-row">
                  <span className="scp-side-panel-title">Settings</span>
                  <button
                    type="button"
                    className="unstyled-button scp-side-panel-icon-button"
                    onClick={hooks.toggleAllItems}
                    title={
                      hooks.expandedItems.length ? 'Collapse all' : 'Expand all'
                    }
                    aria-label={
                      hooks.expandedItems.length ? 'Collapse all' : 'Expand all'
                    }
                  >
                    <ToggleAllIcon className="scp-side-panel-svg" aria-hidden />
                  </button>
                </div>
                <Search
                  className="scp-side-panel-search"
                  placeholder="Search"
                  ariaLabel="Search settings"
                  value={hooks.searchQuery}
                  onChange={hooks.handleSearch}
                  onClear={hooks.handleClearSearch}
                />
                <nav className="scp-side-panel-scrollable">
                  {hooks.filteredNavItems.length > 0 ? (
                    hooks.filteredNavItems.map((item) => {
                      const normalizedQuery = hooks.searchQuery
                        .trim()
                        .toLowerCase()
                      const itemMatches =
                        !!normalizedQuery &&
                        (item.id.toLowerCase().includes(normalizedQuery) ||
                          item.name.toLowerCase().includes(normalizedQuery))
                      const isExpanded = hooks.expandedItems.includes(item.id)
                      const SectionIcon = SETTINGS_NAV_ICONS[item.id]

                      return (
                        <div
                          className="scp-side-panel-item-expand"
                          key={item.id}
                        >
                          <button
                            type="button"
                            className={`unstyled-button scp-side-panel-item${
                              item.items.length === 0 &&
                              hooks.activeItem?.id === item.id
                                ? ' active'
                                : ''
                            }`}
                            onClick={(e) => hooks.toggleItem(e, item.id)}
                            aria-expanded={
                              item.items.length > 0 ? isExpanded : undefined
                            }
                          >
                            <span className="scp-side-panel-item-label">
                              {SectionIcon ? (
                                <SectionIcon
                                  className="scp-spi-icon scp-side-panel-svg"
                                  aria-hidden
                                />
                              ) : (
                                <span className={`scp-spi-icon ${item.icon}`} />
                              )}
                              <span className="scp-side-panel-item-text">
                                {highlightText(item.name, itemMatches)}
                              </span>
                            </span>
                            {item.items.length > 0 && (
                              <CaretIcon
                                className={`scp-side-panel-caret scp-side-panel-svg${
                                  isExpanded ? ' is-expanded' : ''
                                }`}
                                aria-hidden
                              />
                            )}
                          </button>
                          {isExpanded &&
                            item.items.map((subItem) => {
                              const subItemMatches =
                                !!normalizedQuery &&
                                subItem.name
                                  .toLowerCase()
                                  .includes(normalizedQuery)

                              return (
                                <button
                                  type="button"
                                  className={`unstyled-button scp-side-panel-sub-item ${
                                    hooks.activeItem?.id === subItem.id
                                      ? 'active'
                                      : ''
                                  }`}
                                  key={`${item.id}-${subItem.id}`}
                                  onClick={(e) =>
                                    hooks.onSubItemClick(e, subItem)
                                  }
                                >
                                  <span>
                                    {highlightText(
                                      subItem.name,
                                      subItemMatches,
                                    )}
                                  </span>
                                </button>
                              )
                            })}
                        </div>
                      )
                    })
                  ) : (
                    <EmptyState
                      iconClass="mgc_search_line"
                      title="No settings found"
                      description="Try a different search term."
                      className="scp-side-panel-empty"
                    />
                  )}
                </nav>
              </div>
              <div className="scp-side-panel-footer">
                <span className="scp-side-panel-footer-pill">
                  HIPAA Compliant
                </span>
                <span>
                  {APP_VERSION ? `ENAMELAI ver. ${APP_VERSION}` : 'ENAMELAI'}
                </span>
              </div>
            </aside>
          )}
          <section className="scp-main-card">
            <div className="scp-mc-header">
              <div className="settings-title">{hooks.headerTitle}</div>
              <div className="component-buttons">{hooks.headerButtons}</div>
            </div>
            <div className="scp-main-content">
              <Outlet context={outletContext} />
            </div>
          </section>
        </div>
      </div>
    </SettingsHeaderProvider>
  )
}

export default Settings
